/**
 * The overview, second edition: the cards of the dashboard, each written once
 * for both layouts (`compact` is the phone).
 *
 * The desktop arranges them in rows that wrap -- the trend beside where the
 * money sits, categories beside what needs attention, the review beside the
 * simulator's card -- and the phone stacks them in the order a thumb reads.
 */

import { h } from '../vdom.js';
import { localDay, currentMonth, shiftMonth, CATEGORY_COLORS } from '../../../shared/model.js';
import { C, MONO, css, tap, button, emptyLine, expenseLabel, alertText } from './common.js';
import {
  whatIfFor, CASH, DANGER_TEXT, DANGER_BODY, DANGER_BG, DANGER_LINE, GAIN_BG, GAIN_LINE, GAIN_LABEL
} from './whatif.js';

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
/* Where a finger went down on the phone's trend, to tell a tap from a drag. */
let scrub = null;
const pct = ratio => (ratio * 100).toFixed(2) + '%';
const HATCH = (a, b) => `repeating-linear-gradient(135deg, ${a} 0 ${b}px, transparent ${b}px ${b * 2}px)`;
const OVER = size => `repeating-linear-gradient(135deg, rgba(239,78,67,.85) 0 ${size}px, rgba(239,78,67,.45) ${size}px ${size * 2}px)`;

/** A card of the overview: the radius and padding of the layout it is in. */
const box = (compact, style, ...children) => h('div', {
  style: css({
    padding: compact ? '14px' : '18px 20px', border: `1px solid ${C.stroke}`,
    borderRadius: compact ? '16px' : '14px', background: C.card,
    display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '14px', minWidth: 0,
    ...style
  })
}, ...children);

const title = (compact, text) => h('div', {
  style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700, letterSpacing: '-0.2px' })
}, text);

const label = (text, compact, extra = {}) => h('div', {
  style: css({
    fontFamily: MONO, fontSize: compact ? '10px' : '10.5px', letterSpacing: compact ? '1.1px' : '1.2px',
    color: C.muted, ...extra
  })
}, text);

const swatch = (style, text) => h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '7px' }) },
  h('span', { style: css(style) }), text);

/** "3 paź": the day and the month, without the year a 45-day list never needs. */
const shortDay = (t, iso) => {
  const [, month, day] = String(iso || '').split('-');
  return parseInt(day, 10) + ' ' + t.months[parseInt(month, 10) - 1];
};

const addDays = (iso, days) => {
  const date = new Date(iso + 'T00:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// ------------------------------------------------------------------ KPIs ---

export function kpiRow({ metrics, t, fmt, state, compact = false }) {
  const s = t.shell;
  const k = metrics.totals;
  const name = s.ovMonthName(state.month);
  const prev = shiftMonth(state.month, -1);
  const top = metrics.topCategory;
  const share = top && k.expenses > 0 ? Math.round((top.value / k.expenses) * 100) + '%' : null;
  const rate = clamp(k.savingsRate || 0, 0, 1);
  const owing = (metrics.debtRows || []).filter(row => row.balance > 0).length;

  const tile = (kick, value, sub, { valueColor = C.text, hint = null } = {}) => h('div', {
    title: hint,
    style: css({
      padding: compact ? '12px 13px' : '16px 18px', border: `1px solid ${C.stroke}`,
      borderRadius: '14px', background: C.card, display: 'flex', flexDirection: 'column',
      gap: compact ? '5px' : '8px', minWidth: 0
    })
  },
    label(kick, compact),
    h('div', {
      style: css({
        fontFamily: MONO, fontSize: compact ? '18px' : '27px', fontWeight: 700,
        letterSpacing: compact ? 0 : '-0.6px', color: valueColor, whiteSpace: 'nowrap'
      })
    }, value),
    sub
  );
  const line = (text, color = C.textDim) => h('div', {
    style: css({ fontSize: compact ? '11.5px' : '12.5px', color, lineHeight: 1.4 })
  }, text);

  const incomeSub = k.hasPrevIncome
    ? line(compact ? s.ovMonthOnMonth(fmt.signed(k.incomeDelta)) : t.fx.vsMonth(fmt.signed(k.incomeDelta), prev),
      k.incomeDelta >= 0 ? C.accent : DANGER_TEXT)
    : line(t.fx.firstMonth, C.muted);
  const spendSub = share
    ? line(compact
      ? s.ovTopShareShort(share, expenseLabel(t, top.key))
      : s.ovTopShare(share, expenseLabel(t, top.key), fmt.money(top.value)))
    : line(t.fx.nothing, C.muted);
  const savedSub = h('div', { style: css({ display: 'flex', alignItems: 'center', gap: compact ? '7px' : '10px' }) },
    h('div', { style: css({ flex: 1, height: compact ? '5px' : '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
      h('div', { style: css({ width: pct(rate), height: '100%', borderRadius: '3px', background: k.saved >= 0 ? C.accent : C.danger }) })),
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '11px' : '12px', color: C.textDim, whiteSpace: 'nowrap' }) },
      compact ? Math.round((k.savingsRate || 0) * 100) + '%' : s.ovOfIncome(Math.round((k.savingsRate || 0) * 100) + '%'))
  );
  const debtSub = line(compact
    ? s.ovDebtSubShort(fmt.money(k.debtPayments))
    : s.ovDebtSub(fmt.money(k.debtPayments), owing));

  return h('div', {
    style: css({
      display: 'grid', gap: compact ? '10px' : '14px',
      gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(210px, 1fr))'
    })
  },
    tile(compact ? s.ovIncomeShort : s.ovIncome(name), fmt.money(k.income), incomeSub, { hint: t.fx.incomeForWorked }),
    tile(compact ? s.ovSpendShort : s.ovSpend(name), fmt.money(k.expenses), spendSub),
    tile(compact ? s.ovSavedShort : s.ovSaved(name), fmt.money(k.saved), savedSub,
      { valueColor: k.saved >= 0 ? C.accent : C.danger }),
    tile(compact ? s.ovDebtShort : t.debtOutstanding, fmt.money(k.debtTotal), debtSub,
      { valueColor: k.debtTotal > 0 ? C.danger : C.text })
  );
}

// ----------------------------------------------------------------- trend ---

/**
 * Net worth over the window the engine keeps (twelve months), as a line.
 *
 * Measured months are a solid line, months to come a dashed one at today's
 * prices, and the months before the ledger began are hatched rather than drawn
 * as zero. The area under the line is green above zero and red below it.
 * On a desktop a column is a button to that month; on a phone a finger scrubs
 * the line and the figures above it follow.
 */
export function trendCard({ metrics, t, fmt, state, actions, compact = false }) {
  const s = t.shell;
  const k = metrics.totals;
  const bars = metrics.netBars || [];
  const n = bars.length;
  if (!n) return null;
  /*
   * Nothing on record yet: a flat line at zero with "+0 zł vs September" and a
   * "+1k" axis said nothing true. The card says what will fill it instead.
   */
  if (metrics.isEmpty) {
    return box(compact, compact ? { padding: '14px 14px 12px', gap: '8px' } : { padding: '18px 20px 16px', gap: '12px' },
      title(compact, t.netTrend),
      h('div', {
        key: 'empty',
        style: css({
          minHeight: compact ? '90px' : '160px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '0 12px', border: `1px dashed ${C.stroke}`, borderRadius: '12px',
          fontSize: '12.5px', lineHeight: 1.5, color: C.muted
        })
      }, s.ovTrendEmpty));
  }
  const now = currentMonth();
  const lead = bars.findIndex(p => !p.empty);
  const first = lead === -1 ? n : lead;
  const live = bars.map((p, i) => ({ ...p, i })).filter(p => !p.empty);
  const actual = live.filter(p => p.month <= now);
  const future = live.filter(p => p.month > now);
  const nowIndex = bars.findIndex(p => p.month === now);

  /*
   * Scale: a few round steps. Zero is in view whenever the line comes near it
   * -- crossing it is the story of a ledger in debt -- but not for a fortune
   * that moves by a few per cent: forcing 0 under 450 thousand would flatten
   * a year into a straight line along the top.
   */
  const nets = live.map(p => p.net);
  const rawLo = nets.length ? Math.min(...nets) : 0;
  const rawHi = nets.length ? Math.max(...nets) : 0;
  const far = rawLo > 0 ? rawLo > (rawHi - rawLo) * 3 : rawHi < 0 && -rawHi > (rawHi - rawLo) * 3;
  const values = far ? nets : [...nets, 0];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const rough = (hi - lo) / (compact ? 3 : 5) || Math.max(1000, Math.abs(hi) * 0.05);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map(x => x * magnitude).find(x => x >= rough);
  const baseMin = far ? Math.floor(lo / step) * step - (lo % step === 0 ? step : 0)
    : lo < 0 && -lo < step * 0.25 ? lo - step * 0.12 : Math.floor(lo / step) * step;
  const baseMax = far ? Math.ceil(hi / step) * step + (hi % step === 0 ? step : 0)
    : hi > 0 && hi < step * 0.25 ? hi + step * 0.12 : Math.max(Math.ceil(hi / step) * step, baseMin + step);
  /*
   * The desktop writes a figure beside every month, so the lowest and highest
   * readings need room under and over them -- without it a dip's figure had
   * nowhere to go but up, onto the line falling into it.
   */
  const span = Math.max(hi - lo, step);
  const yMin = compact ? baseMin : Math.min(baseMin, lo - span * 0.24);
  const yMax = compact ? baseMax : Math.max(baseMax, hi + span * 0.16);
  const Y = v => ((yMax - v) / (yMax - yMin)) * 260;
  const X = i => ((i + 0.5) / n) * 1200;
  const top = v => pct((yMax - v) / (yMax - yMin));
  const left = i => pct((i + 0.5) / n);
  /* Where the area under the line ends: zero, or the edge of the chart when zero is off it. */
  const zeroY = clamp(Y(0), 0, 260);

  const axis = v => {
    if (Math.abs(v) < step / 1000) return '0';
    const big = Math.abs(v) >= 1e6;
    const value = Math.abs(v) / (big ? 1e6 : 1e3);
    const decimals = Number.isInteger(value) ? 0 : 1;
    /* A plus only where there is a minus to tell it from. */
    const sign = v < 0 ? '−' : yMin < 0 ? '+' : '';
    /* The phone's axis is 40px wide: "480k", as the design has it, in either language. */
    const unit = compact ? (big ? 'M' : 'k') : (big ? s.wiAxisM : s.wiAxisK);
    return sign + fmt.number(value, decimals) + unit;
  };
  const ticks = [];
  for (let v = Math.ceil(yMax / step) * step; v >= yMin - step / 1000; v -= step) {
    if (v <= yMax + step / 1000) ticks.push(Math.abs(v) < step / 1000 ? 0 : v);
  }

  const path = points => points.map((p, j) => (j ? 'L' : 'M') + X(p.i).toFixed(1) + ',' + Y(p.net).toFixed(1)).join('');
  const clip = compact ? 'ovm' : 'ovd';
  const area = actual.length > 1
    ? path(actual) + `L${X(actual[actual.length - 1].i).toFixed(1)},${zeroY.toFixed(1)}L${X(actual[0].i).toFixed(1)},${zeroY.toFixed(1)}Z`
    : null;
  const ahead = future.length && actual.length ? [actual[actual.length - 1], ...future] : future;

  const svg = h('svg', {
    key: 'svg', viewBox: '0 0 1200 260', preserveAspectRatio: 'none',
    style: css({ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' })
  },
    h('defs', { key: 'defs' },
      h('clipPath', { id: clip + 'Above' }, h('rect', { x: 0, y: -1000, width: 1200, height: zeroY + 1000 })),
      h('clipPath', { id: clip + 'Below' }, h('rect', { x: 0, y: zeroY, width: 1200, height: 2000 }))
    ),
    area && h('path', { key: 'up', d: area, 'clip-path': `url(#${clip}Above)`, fill: 'rgba(61,217,160,.22)' }),
    area && h('path', { key: 'down', d: area, 'clip-path': `url(#${clip}Below)`, fill: 'rgba(239,78,67,.14)' }),
    ahead.length > 1 && h('path', {
      key: 'ahead', d: path(ahead), fill: 'none', stroke: C.muted, 'stroke-width': 2,
      'stroke-dasharray': compact ? '5 5' : '6 6', 'vector-effect': 'non-scaling-stroke'
    }),
    actual.length > 1 && h('path', {
      key: 'line', d: path(actual), fill: 'none', stroke: C.text, 'stroke-width': 2.5,
      'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round', 'stroke-linecap': 'round'
    })
  );

  const grid = ticks.flatMap((v, j) => [
    h('div', {
      key: 'g' + j,
      style: css({
        position: 'absolute', left: 0, right: 0, top: top(v), height: 0,
        borderTop: v === 0 ? `1.5px solid ${C.ghost}` : `1px solid ${C.stroke}`
      })
    }),
    h('div', {
      key: 'gl' + j,
      style: css({
        position: 'absolute', left: compact ? '-44px' : '-64px', width: compact ? '38px' : '56px', top: top(v),
        transform: 'translateY(-50%)', textAlign: 'right', fontFamily: MONO, fontSize: compact ? '10px' : '11px',
        fontWeight: v === 0 ? 700 : 400, color: v === 0 ? C.text : C.muted, whiteSpace: 'nowrap'
      })
    }, axis(v))
  ]);

  const noData = first > 0 && h('div', {
    key: 'nodata',
    style: css({
      position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(first / n), borderRadius: '6px',
      background: HATCH('rgba(130,150,168,.08)', compact ? 5 : 6)
    })
  }, !compact && first >= 3 && h('div', {
    style: css({
      position: 'absolute', left: '50%', top: '44%', transform: 'translate(-50%,-50%)',
      fontSize: '12px', color: C.muted, whiteSpace: 'nowrap'
    })
  }, s.ovNoDataBefore(bars[first]?.month || now)));

  const monthShort = key => t.months[parseInt(key.slice(5, 7), 10) - 1].toUpperCase();

  /** What a month's reading says, for the readout on either layout. */
  const describe = at => {
    const point = bars[at];
    const isFuture = point.month > now;
    const prevPoint = at > 0 ? bars[at - 1] : null;
    let delta = '';
    let deltaColor = C.muted;
    if (point.empty) delta = s.ovNoDataBefore(bars[first]?.month || now);
    else if (isFuture) delta = s.ovAtPrices;
    else if (!prevPoint || prevPoint.empty) delta = s.ovFirstMonth;
    else {
      const d = point.net - prevPoint.net;
      delta = s.ovVsPrev(fmt.signed(d), prevPoint.month);
      deltaColor = d >= 0 ? C.accent : DANGER_TEXT;
    }
    return { point, isFuture, delta, deltaColor };
  };
  /* Pointer movement eases between months rather than jumping. */
  const glide = '.18s cubic-bezier(.22, .61, .36, 1)';
  const viewed = bars.findIndex(p => p.current);

  // ---- phone: one readout, a finger picks the month ----
  if (compact) {
    /* The month on screen, so a tap that opens one shows it here too. */
    const home = viewed >= 0 ? viewed : nowIndex >= 0 ? nowIndex : (actual.length ? actual[actual.length - 1].i : n - 1);
    const at = state.trendHover !== null && state.trendHover < n ? state.trendHover : home;
    const { point, isFuture, delta, deltaColor } = describe(at);
    const indexAt = event => {
      const r = event.currentTarget.getBoundingClientRect();
      return r.width ? clamp(Math.round(((event.clientX - r.left) / r.width) * n - 0.5), 0, n - 1) : null;
    };
    const pick = event => {
      const i = indexAt(event);
      if (i !== null) actions.setTrendHover(i);
    };
    const leave = () => actions.setTrendHover(null);
    /*
     * A tap opens the month under it, as a click does on the desktop; a drag
     * only looks. Told apart by how far the finger travelled, remembered
     * outside the render because every move re-renders this handler.
     */
    const down = event => { scrub = { x: event.clientX }; pick(event); };
    const up = event => {
      const i = indexAt(event);
      const tapped = scrub && Math.abs(event.clientX - scrub.x) < 8;
      scrub = null;
      if (tapped && i !== null) actions.goToMonth(bars[i].month);
      if (event.pointerType !== 'mouse' || tapped) leave();
    };

    return box(true, { padding: '14px 14px 12px', gap: '8px' },
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }) },
        title(true, t.netTrend),
        h('div', { style: css({ fontSize: '11px', color: C.muted }) }, at === home ? s.ovSlide : s.ovPicked)
      ),
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '9px', flexWrap: 'wrap' }) },
        h('div', { style: css({ fontFamily: MONO, fontSize: '11px', letterSpacing: '1px', color: C.muted }) },
          s.monthFull(point.month).toUpperCase()),
        isFuture && h('div', {
          key: 'tag',
          style: css({ padding: '2px 6px', borderRadius: '5px', background: C.stroke, fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.8px', color: C.textDim })
        }, s.ovForecastTag)
      ),
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '9px', flexWrap: 'wrap', minHeight: '30px' }) },
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '24px', fontWeight: 700, letterSpacing: '-0.5px',
            color: point.empty ? C.muted : point.net < 0 ? DANGER_TEXT : C.accent
          })
        }, point.empty ? '—' : (isFuture ? '≈ ' : '') + fmt.money(point.net)),
        h('div', { style: css({ fontSize: '12px', color: deltaColor }) }, delta)
      ),
      h('div', { style: css({ position: 'relative', height: '170px', margin: '6px 4px 0 44px' }) },
        noData,
        ...grid,
        svg,
        nowIndex >= 0 && !bars[nowIndex].empty && h('div', {
          key: 'now',
          style: css({
            position: 'absolute', left: left(nowIndex), top: top(bars[nowIndex].net), width: '11px', height: '11px',
            margin: '-5.5px 0 0 -5.5px', borderRadius: '50%', background: bars[nowIndex].net < 0 ? C.danger : C.accent,
            boxShadow: `0 0 0 3px ${C.card}`
          })
        }),
        h('div', {
          key: 'cursor',
          style: css({
            position: 'absolute', top: '-6px', bottom: '-6px', left: left(at), width: 0, borderLeft: `1px solid ${C.muted}`,
            pointerEvents: 'none', transition: `left ${glide}`
          })
        }),
        !point.empty && h('div', {
          key: 'ring',
          style: css({
            position: 'absolute', left: left(at), top: top(point.net), width: '15px', height: '15px',
            margin: '-7.5px 0 0 -7.5px', borderRadius: '50%', border: `3px solid ${C.text}`, background: C.card,
            boxSizing: 'border-box', pointerEvents: 'none', transition: `left ${glide}, top ${glide}`
          })
        }),
        h('div', {
          key: 'surface',
          role: 'presentation',
          onpointerdown: down, onpointermove: pick, onpointerleave: leave,
          onpointercancel: () => { scrub = null; leave(); },
          onpointerup: up,
          style: css({ position: 'absolute', top: '-12px', bottom: '-12px', left: 0, right: 0, cursor: 'crosshair', touchAction: 'none' })
        })
      ),
      h('div', { style: css({ display: 'flex', margin: '6px 4px 0 44px' }) },
        ...bars.map((p, i) => h('div', {
          key: 'x' + i,
          style: css({
            flex: '1 1 0', minWidth: 0, textAlign: 'center', fontFamily: MONO, fontSize: '9px',
            fontWeight: i === at || i === nowIndex ? 700 : 400,
            color: i === at ? C.text : i === nowIndex ? DANGER_TEXT : p.empty ? C.ghost : C.muted
          })
        }, monthShort(p.month)))
      ),
      legend(true)
    );
  }

  // ---- desktop: every measured month labelled, columns lead to their month ----
  const range = Math.max(1, hi - lo);
  /*
   * Short figures once the months crowd in: "439 tys." fits a column where
   * "439 197 zł" runs into its neighbour.
   */
  const crowded = actual.length > 6;
  const short = v => {
    const a = Math.abs(v);
    if (!crowded || a < 10000) return fmt.money(v);
    const big = a >= 1e6;
    const value = a / (big ? 1e6 : 1e3);
    /* The same minus as every other amount in the app (Intl's), so "-18 tys." sits right beside "-3800 zł". */
    return (v < 0 ? '-' : '') + fmt.number(value, value >= 100 ? 0 : 1) + (big ? s.wiAxisM : s.wiAxisK);
  };
  /* A month that reads the same as the one before it says nothing new: its dot stays, its figure goes. */
  const repeats = p => p.i > 0 && !bars[p.i - 1].empty && short(bars[p.i - 1].net) === short(p.net);
  const labels = actual.filter(p => p.i !== nowIndex).flatMap(p => {
    const prevPoint = p.i > 0 && !bars[p.i - 1].empty ? bars[p.i - 1] : null;
    const nextPoint = p.i < n - 1 && !bars[p.i + 1].empty ? bars[p.i + 1] : null;
    const jump = prevPoint ? p.net - prevPoint.net : 0;
    const big = prevPoint && Math.abs(jump) > range * 0.3;
    /*
     * The figure goes on the side the line does not: a segment arrives from
     * the neighbour on the left and leaves for the one on the right, and each
     * takes the quarter around the dot facing that neighbour. A dip's figure
     * goes under it, a peak's over it, and on a slope it sits over the side
     * the line is not climbing through.
     */
    const prevUp = prevPoint ? prevPoint.net > p.net : null;
    const nextUp = nextPoint ? nextPoint.net > p.net : null;
    const place = prevUp === null && nextUp === null ? 'above'
      : prevUp === null ? (nextUp ? 'below' : 'above')
        : nextUp === null ? (prevUp ? 'below' : 'above')
          : prevUp && nextUp ? 'below'
            : !prevUp && !nextUp ? 'above'
              : prevUp ? 'right' : 'left';
    return [
      h('div', {
        key: 'pd' + p.i,
        style: css({
          position: 'absolute', left: left(p.i), top: top(p.net), width: '9px', height: '9px',
          margin: '-4.5px 0 0 -4.5px', borderRadius: '50%', background: C.text, boxShadow: `0 0 0 3px ${C.card}`, pointerEvents: 'none'
        })
      }),
      h('div', {
        key: 'pl' + p.i,
        style: css({
          position: 'absolute', left: left(p.i), top: top(p.net),
          transform: {
            below: 'translate(-50%, 12px)',
            above: 'translate(-50%, calc(-100% - 12px))',
            right: 'translate(10px, calc(-100% - 8px))',
            left: 'translate(calc(-100% - 10px), calc(-100% - 8px))'
          }[place],
          display: 'flex', flexDirection: 'column', gap: '2px',
          alignItems: place === 'right' ? 'flex-start' : place === 'left' ? 'flex-end' : 'center',
          whiteSpace: 'nowrap', pointerEvents: 'none'
        })
      },
        !repeats(p) && h('span', { key: 'v', style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.textDim }) }, short(p.net)),
        big && h('span', { key: 'jump', style: css({ fontSize: '11px', color: jump >= 0 ? C.accent : DANGER_TEXT }) },
          s.ovVsPrev(fmt.signed(jump), prevPoint.month))
      )
    ];
  });

  const nowPoint = nowIndex >= 0 && !bars[nowIndex].empty ? bars[nowIndex] : null;
  const nowMarks = nowPoint ? [
    h('div', {
      key: 'nowdot',
      style: css({
        position: 'absolute', left: left(nowIndex), top: top(nowPoint.net), width: '13px', height: '13px',
        margin: '-6.5px 0 0 -6.5px', borderRadius: '50%', background: nowPoint.net < 0 ? C.danger : C.accent,
        boxShadow: `0 0 0 4px ${nowPoint.net < 0 ? 'rgba(239,78,67,.22)' : 'rgba(61,217,160,.22)'}`, pointerEvents: 'none'
      })
    }),
    h('div', {
      key: 'nowtag',
      style: css({
        position: 'absolute', left: `calc(${left(nowIndex)} - 8px)`, top: `calc(${top(nowPoint.net)} + 14px)`,
        display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', pointerEvents: 'none',
        ...((yMax - nowPoint.net) / (yMax - yMin) > 0.8 ? { top: `calc(${top(nowPoint.net)} - 34px)` } : {})
      })
    },
      h('span', {
        style: css({
          padding: '2px 6px', borderRadius: '5px', background: nowPoint.net < 0 ? DANGER_BG : GAIN_BG,
          fontFamily: MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.8px', color: nowPoint.net < 0 ? DANGER_TEXT : C.accent
        })
      }, s.ovNow),
      h('span', { style: css({ fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: nowPoint.net < 0 ? DANGER_TEXT : C.accent }) },
        fmt.money(nowPoint.net))
    )
  ] : [];

  const last = future[future.length - 1];
  const futureMarks = last ? [
    h('div', {
      key: 'fdot',
      style: css({
        position: 'absolute', left: left(last.i), top: top(last.net), width: '9px', height: '9px', margin: '-4.5px 0 0 -4.5px',
        borderRadius: '50%', border: `2px solid ${C.muted}`, background: C.card, boxSizing: 'border-box', pointerEvents: 'none'
      })
    }),
    h('div', {
      key: 'flabel',
      style: css({
        position: 'absolute', right: 0, top: `calc(${top(last.net)} - 30px)`, fontFamily: MONO, fontSize: '12px',
        color: C.textDim, whiteSpace: 'nowrap', pointerEvents: 'none'
      })
    }, '≈ ' + short(last.net))
  ] : [];

  /*
   * Pointing at a month shows what it read, as the phone's readout does: a
   * line that eases to the month, a ring on its reading, and the figures in a
   * box beside it. A click on the column still opens that month.
   */
  const hovered = state.trendHover !== null && state.trendHover < n ? state.trendHover : null;
  const hoverMarks = hovered === null ? [] : (() => {
    const { point, isFuture, delta, deltaColor } = describe(hovered);
    const ratio = (hovered + 0.5) / n;
    return [
      h('div', {
        key: 'hcur',
        style: css({
          position: 'absolute', top: '-10px', bottom: '-10px', left: left(hovered), width: 0,
          borderLeft: `1px solid ${C.muted}`, pointerEvents: 'none', transition: `left ${glide}`
        })
      }),
      !point.empty && h('div', {
        key: 'hring',
        style: css({
          position: 'absolute', left: left(hovered), top: top(point.net), width: '15px', height: '15px',
          margin: '-7.5px 0 0 -7.5px', borderRadius: '50%', border: `3px solid ${C.text}`, background: C.card,
          boxSizing: 'border-box', pointerEvents: 'none', transition: `left ${glide}, top ${glide}`
        })
      }),
      h('div', {
        key: 'htip',
        class: 'pt-pop',
        style: css({
          position: 'absolute', top: '-6px', left: left(hovered), zIndex: 3, pointerEvents: 'none',
          transform: ratio > 0.62 ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)',
          transition: `left ${glide}, transform ${glide}`,
          minWidth: '200px', padding: '11px 13px', borderRadius: '10px', background: C.bg,
          border: `1px solid ${C.strokeHi}`, boxShadow: '0 12px 28px rgba(0,0,0,.45)',
          display: 'flex', flexDirection: 'column', gap: '6px'
        })
      },
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
          h('span', { style: css({ fontFamily: MONO, fontSize: '10px', letterSpacing: '1.2px', color: C.muted }) },
            s.monthFull(point.month).toUpperCase()),
          isFuture && h('span', {
            key: 'f',
            style: css({ padding: '1px 5px', borderRadius: '4px', background: C.stroke, fontFamily: MONO, fontSize: '9px', letterSpacing: '.8px', color: C.textDim })
          }, s.ovForecastTag)
        ),
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '18px', fontWeight: 700, whiteSpace: 'nowrap',
            color: point.empty ? C.muted : point.net < 0 ? DANGER_TEXT : C.accent
          })
        }, point.empty ? '—' : (isFuture ? '≈ ' : '') + fmt.money(point.net)),
        h('div', { style: css({ fontSize: '12px', color: deltaColor, whiteSpace: 'nowrap' }) }, delta),
        !point.current && h('div', {
          key: 'go',
          style: css({ fontSize: '11px', color: C.faint, paddingTop: '4px', borderTop: `1px solid ${C.stroke}` })
        }, s.ovClickToOpen)
      )
    ];
  })();

  const columns = bars.map((p, i) => h('div', {
    key: 'c' + i,
    ...tap(() => actions.goToMonth(p.month), s.monthFull(p.month)),
    class: 'pt-tap',
    'aria-current': p.current ? 'true' : null,
    onpointerenter: () => actions.setTrendHover(i),
    onfocus: () => actions.setTrendHover(i),
    style: css({
      position: 'absolute', top: '-12px', bottom: '-12px', left: pct(i / n), width: pct(1 / n), cursor: 'pointer',
      borderRadius: '6px', background: p.current ? 'rgba(130,150,168,.06)' : 'transparent'
    })
  }));

  return box(false, { padding: '18px 20px 16px', gap: '12px' },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }) },
      title(false, t.netTrend),
      h('div', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.ovTrendSub),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '12.5px', color: k.hasPrevNet ? (k.netDelta >= 0 ? C.accent : DANGER_TEXT) : C.muted })
      }, k.hasPrevNet ? t.fx.vsMonth(fmt.signed(k.netDelta), k.prevNetMonth) : t.fx.firstMonth)
    ),
    h('div', {
      onpointerleave: () => actions.setTrendHover(null),
      style: css({ position: 'relative', height: '260px', margin: '30px 8px 0 64px' })
    },
      ...columns,
      noData,
      ...grid,
      svg,
      ...labels,
      ...futureMarks,
      ...nowMarks,
      ...hoverMarks
    ),
    h('div', { style: css({ position: 'relative', height: '18px', margin: '16px 8px 0 64px' }) },
      ...bars.map((p, i) => h('span', {
        key: 'x' + i,
        style: css({
          position: 'absolute', left: left(i), transform: 'translateX(-50%)', fontFamily: MONO, fontSize: '11px',
          fontWeight: i === nowIndex || p.current ? 700 : 400,
          color: i === nowIndex ? DANGER_TEXT : p.current ? C.text : p.empty ? C.ghost : (p.month <= now ? C.textDim : C.muted)
        })
      }, monthShort(p.month)))
    ),
    legend(false)
  );

  function legend(phone) {
    return h('div', {
      style: css({
        display: 'flex', alignItems: 'center', gap: phone ? '12px' : '18px', flexWrap: 'wrap', paddingTop: '8px',
        borderTop: `1px solid ${C.line}`, fontSize: phone ? '11px' : '12px', color: C.textDim
      })
    },
      swatch({ width: phone ? '14px' : '18px', borderTop: `2.5px solid ${C.text}` }, s.ovActual),
      future.length > 0 && h('span', { key: 'f' },
        swatch({ width: phone ? '14px' : '18px', borderTop: `2px dashed ${C.muted}` }, phone ? s.ovForecastShort : s.ovForecast)),
      !phone && live.some(p => p.net < 0) && h('span', { key: 'b' },
        swatch({ width: '14px', height: '10px', borderRadius: '2px', background: 'rgba(239,78,67,.25)' }, s.ovBelowZero)),
      first > 0 && h('span', { key: 'n' },
        swatch({ width: phone ? '11px' : '14px', height: phone ? '8px' : '10px', borderRadius: '2px', background: HATCH('rgba(130,150,168,.4)', phone ? 2 : 3) }, s.ovNoData))
    );
  }
}

// --------------------------------------------------- where the money is ---

export function moneyCard({ metrics, t, fmt, actions, compact = false }) {
  const s = t.shell;
  const k = metrics.totals;
  const debt = Math.max(0, k.debtTotal);
  const cash = k.cash;
  const assets = Math.max(0, cash) + Math.max(0, k.invValue);
  const side = Math.max(1, debt, assets);
  /*
   * The headline's figure. A closed month's net worth is the one recorded when
   * it ended; the parts below use today's cash, so adding them up gave a third
   * number that was never true on any date -- beside the header's.
   */
  const net = k.netRecorded ? k.netWorth : assets - debt + Math.min(0, cash);
  const times = debt > 0 ? assets / debt : 0;
  /* Past twice the debt a percentage stops reading as one: "15×", not "1542%". */
  const cover = debt <= 0 ? null : times >= 2 ? fmt.number(times, times >= 10 ? 0 : 1) + '×' : Math.round(times * 100) + '%';
  const height = compact ? 20 : 26;

  const bar = h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: compact ? '6px' : '8px' }) },
    h('div', { style: css({ display: 'flex', height: height + 'px' }) },
      h('div', { style: css({ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end' }) },
        h('div', { style: css({ width: pct(debt / side), height: '100%', borderRadius: '6px 0 0 6px', background: C.danger }) })),
      h('div', { style: css({ width: '2px', background: C.text }) }),
      h('div', { style: css({ flex: '1 1 0', display: 'flex' }) },
        h('div', { style: css({ width: pct(Math.max(0, cash) / side), height: '100%', background: CASH }) }),
        h('div', { style: css({ width: pct(Math.max(0, k.invValue) / side), height: '100%', borderRadius: '0 6px 6px 0', background: C.accent }) }))
    ),
    h('div', { style: css({ display: 'flex', justifyContent: 'space-between', fontSize: compact ? '11px' : '12px', color: compact ? C.muted : C.textDim }) },
      h('span', null, s.ovLeftDebts), h('span', null, s.ovRightAssets))
  );

  const row = (color, name, note, value, { noteColor = C.muted, valueColor = C.text, onNote = null } = {}) => {
    const noteNode = note && h(onNote ? 'button' : 'span', {
      key: 'note',
      ...(onNote ? { type: 'button', onclick: onNote } : {}),
      style: css({
        fontSize: compact ? '11px' : '12px', color: noteColor, background: 'none', border: 'none', padding: 0,
        cursor: onNote ? 'pointer' : 'default', fontFamily: 'inherit', textAlign: 'left'
      })
    }, note);
    return h('div', {
      style: css({
        display: 'flex', alignItems: 'center', gap: compact ? '9px' : '10px', borderTop: `1px solid ${C.line}`,
        ...(compact ? { minHeight: '44px' } : { padding: '10px 0' })
      })
    },
      h('span', { style: css({ width: compact ? '8px' : '9px', height: compact ? '8px' : '9px', flex: '0 0 auto', borderRadius: '2px', background: color }) }),
      compact
        ? h('div', { key: 'n', style: css({ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '13px' }) }, name), noteNode)
        : h('span', { key: 'n', style: css({ flex: 1, fontSize: '13px' }) }, name),
      !compact && noteNode,
      h('span', {
        style: css({ width: compact ? 'auto' : '96px', textAlign: 'right', fontFamily: MONO, fontSize: '13px', color: valueColor, whiteSpace: 'nowrap' })
      }, fmt.money(value))
    );
  };

  const rows = h('div', { style: css({ display: 'flex', flexDirection: 'column' }) },
    row(C.danger, s.ovDebts, debt > 0 ? s.ovInstalments(fmt.money(k.debtPayments)) : null, debt),
    row(C.accent, s.ovInvestments, k.invValue > 0 ? (compact ? fmt.signed(k.invGain) : s.ovUnrealised(fmt.signed(k.invGain))) : null,
      k.invValue, { noteColor: k.invGain >= 0 ? C.accent : DANGER_TEXT }),
    row(CASH, s.ovCash, cash === 0 ? s.ovFillCash : null, cash,
      { noteColor: C.accent, valueColor: cash === 0 ? C.muted : C.text, onNote: cash === 0 ? () => actions.openSettings('finance') : null })
  );

  if (compact) {
    return box(true, {},
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }) },
        title(true, t.whereMoney),
        cover && h('div', { key: 'cover', style: css({ fontSize: '11.5px', color: C.textDim }) },
          s.ovCover + ' ', h('span', { style: css({ fontFamily: MONO, fontWeight: 700, color: C.text }) }, cover))
      ),
      bar,
      rows
    );
  }

  return box(false, { gap: '16px', height: '100%', boxSizing: 'border-box' },
    title(false, t.whereMoney),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
      label(s.ovNet, false),
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '30px', fontWeight: 700, letterSpacing: '-0.8px', color: net < 0 ? DANGER_TEXT : C.accent })
      }, fmt.money(net)),
      k.netRecorded && h('div', { key: 'rec', style: css({ fontSize: '11.5px', lineHeight: 1.4, color: C.muted }) }, s.ovNetRecorded)
    ),
    bar,
    rows,
    h('div', { style: css({ flex: 1 }) }),
    h('div', {
      style: css({ padding: '11px 13px', borderRadius: '10px', background: C.panel, fontSize: '12.5px', lineHeight: 1.5, color: C.textDim })
    }, debt <= 0
      ? s.ovNoDebt
      : assets >= debt * 2
        ? s.ovCoverTimes(fmt.number(assets / debt, assets / debt >= 10 ? 0 : 1))
        : [s.ovCoverPre, h('span', { key: 'v', style: css({ fontFamily: MONO, fontWeight: 700, color: C.text }) }, cover), s.ovCoverPost])
  );
}

// ------------------------------------------------------------ categories ---

export function categoriesCard({ metrics, t, fmt, state, compact = false }) {
  const s = t.shell;
  const rows = metrics.catBars || [];
  const hasAverage = (metrics.catAverageMonths || 0) > 0;
  const total = metrics.totals.expenses;
  const avgTotal = metrics.catAverageTotal;

  const judge = row => {
    if (row.average === null || row.average === undefined) return { text: '', color: C.muted, over: false };
    const over = row.value > row.average * 1.2;
    const under = row.value < row.average * 0.9;
    return {
      over,
      text: over || under ? fmt.signed(row.value - row.average) : s.ovAboutAvg,
      color: over ? DANGER_TEXT : under ? C.accent : C.muted
    };
  };

  const track = (row, height) => {
    const verdict = judge(row);
    const avg = row.averageRatio;
    return h('div', { style: css({ position: 'relative', height: height + 'px', borderRadius: height / 2 + 'px', background: C.stroke }) },
      h('div', {
        style: css({
          position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(row.ratio), minWidth: compact ? '5px' : '6px',
          borderRadius: height / 2 + 'px', background: row.color
        })
      }),
      verdict.over && avg !== null && h('div', {
        key: 'over',
        style: css({
          position: 'absolute', left: pct(avg), width: pct(Math.max(0, row.ratio - avg)), top: 0, bottom: 0,
          borderRadius: `0 ${height / 2}px ${height / 2}px 0`, background: OVER(compact ? 3 : 4)
        })
      }),
      avg !== null && avg !== undefined && h('div', {
        key: 'avg',
        style: css({ position: 'absolute', left: pct(avg), top: '-4px', bottom: '-4px', width: '2px', background: C.text })
      })
    );
  };

  const header = h('div', {
    style: css({ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', justifyContent: compact ? 'space-between' : 'flex-start' })
  },
    title(compact, t.spendByCat),
    !compact && h('div', { key: 'm', style: css({ fontSize: '12.5px', color: C.muted }) }, s.monthFull(state.month)),
    !compact && h('div', { key: 'sp', style: css({ flex: 1 }) }),
    hasAverage && h('div', { key: 'lg', style: css({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: compact ? '11px' : '12px', color: C.textDim }) },
      h('span', { style: css({ width: '2px', height: compact ? '10px' : '12px', background: C.text }) }),
      compact ? s.ovAvg : s.ovAvg3)
  );

  const footer = rows.length > 0 && h('div', {
    key: 'foot',
    style: css({
      display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', paddingTop: compact ? '10px' : '12px',
      borderTop: `1px solid ${C.line}`, fontSize: compact ? '12px' : '12.5px', color: C.textDim
    })
  },
    h('span', null, s.ovTotal + ' ', h('span', { style: css({ fontFamily: MONO, fontWeight: 700, color: C.text }) }, fmt.money(total))),
    avgTotal !== null && avgTotal !== undefined && h('span', { key: 'avg' },
      compact
        ? h('span', { style: css({ color: total > avgTotal ? DANGER_TEXT : C.accent }) }, s.ovVsAvg(fmt.signed(total - avgTotal)))
        : [
          s.ovAvgTotal + ' ',
          h('span', { key: 'a', style: css({ fontFamily: MONO, color: C.text }) }, fmt.money(avgTotal)),
          ' · ',
          h('span', { key: 'd', style: css({ color: total > avgTotal ? DANGER_TEXT : C.accent }) }, fmt.signed(total - avgTotal))
        ])
  );

  const note = rows.length > 0 && (hasAverage
    ? !compact && rows.some(row => judge(row).over) && h('div', {
      key: 'note', style: css({ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.muted })
    }, h('span', { style: css({ width: '14px', height: '10px', borderRadius: '2px', background: OVER(3) }) }), s.ovOverNote)
    : h('div', { key: 'note', style: css({ fontSize: '12px', color: C.muted }) }, s.ovNoAverage));

  if (compact) {
    return box(true, {},
      header,
      rows.length === 0 && emptyLine(t.shell.emptyList),
      ...rows.map(row => {
        const verdict = judge(row);
        return h('div', { key: row.key, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
          h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '8px' }) },
            h('span', { style: css({ width: '8px', height: '8px', flex: '0 0 8px', borderRadius: '2px', background: row.color }) }),
            h('span', { style: css({ flex: 1, minWidth: 0, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, expenseLabel(t, row.key)),
            h('span', { style: css({ fontFamily: MONO, fontSize: '13px' }) }, fmt.money(row.value)),
            h('span', { style: css({ width: '76px', textAlign: 'right', fontSize: '11.5px', color: verdict.color }) }, verdict.text)
          ),
          track(row, 8)
        );
      }),
      footer,
      note
    );
  }

  return box(false, {},
    header,
    rows.length === 0
      ? emptyLine(t.shell.emptyList)
      : h('div', {
        style: css({ display: 'grid', gridTemplateColumns: 'minmax(0,150px) minmax(0,1fr) 92px 104px', gap: '10px 14px', alignItems: 'center' })
      }, ...rows.flatMap(row => {
        const verdict = judge(row);
        return [
          h('div', {
            key: row.key + 'n', title: expenseLabel(t, row.key),
            style: css({ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })
          }, expenseLabel(t, row.key)),
          h('div', { key: row.key + 't' }, track(row, 12)),
          h('div', { key: row.key + 'v', style: css({ textAlign: 'right', fontFamily: MONO, fontSize: '13px', whiteSpace: 'nowrap' }) }, fmt.money(row.value)),
          h('div', { key: row.key + 'd', style: css({ textAlign: 'right', fontSize: '12px', color: verdict.color, whiteSpace: 'nowrap' }) }, verdict.text)
        ];
      })),
    footer,
    note
  );
}

// ------------------------------------------------------------- attention ---

export function attentionCard({ metrics, t, fmt, compact = false }) {
  const s = t.shell;
  const item = (alert, index) => {
    const copy = alertText(alert, { t, fmt, mobile: compact });
    const tone = alert.tone === 'danger'
      ? { bg: DANGER_BG, border: DANGER_LINE, body: DANGER_BODY }
      : alert.tone === 'good'
        ? { bg: GAIN_BG, border: GAIN_LINE, body: '#B4CCC4' }
        : { bg: compact ? C.card : C.panel, border: C.stroke, body: C.textDim };
    return h('div', {
      key: alert.kind + index,
      style: css({
        display: 'flex', gap: compact ? '10px' : '12px', alignItems: 'flex-start',
        padding: compact ? '12px 13px' : '13px 14px', borderRadius: compact ? '13px' : '11px',
        background: tone.bg, border: `1px solid ${tone.border}`
      })
    },
      h('span', {
        style: css({
          width: '8px', height: '8px', borderRadius: '50%', flex: '0 0 8px', marginTop: compact ? '5px' : '6px',
          background: alert.tone === 'danger' ? C.danger : alert.tone === 'good' ? C.accent : C.gold
        })
      }),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: compact ? '3px' : '4px', minWidth: 0 }) },
        h('div', { style: css({ fontSize: compact ? '13px' : '13.5px', fontWeight: 600 }) }, copy.title),
        h('div', { style: css({ fontSize: compact ? '12px' : '12.5px', lineHeight: 1.5, color: tone.body }) }, copy.body)
      )
    );
  };

  const empty = metrics.alerts.length === 0 && (metrics.isEmpty
    ? h('div', { key: 'e' },
      h('div', { style: css({ fontSize: '12.5px', fontWeight: 600, color: C.textDim }) }, s.emptyTitle),
      emptyLine(s.emptyBody, { marginTop: '4px' }))
    : emptyLine(s.nothingToFlag));

  if (compact) {
    if (metrics.alerts.length === 0 && !metrics.isEmpty) return null;
    return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
      h('div', { style: css({ fontSize: '14px', fontWeight: 700, padding: '4px 2px 0' }) }, t.attention),
      empty,
      ...metrics.alerts.map(item)
    );
  }
  return box(false, { gap: '12px' },
    title(false, t.attention),
    empty,
    ...metrics.alerts.map(item)
  );
}

// -------------------------------------------------------------- upcoming ---

export function upcomingCard({ t, fmt, state, actions, compact = false }) {
  const s = t.shell;
  const { rows, total, days, unconverted } = actions.upcomingCharges();
  const base = state.data?.settings?.baseCurrency;
  const today = localDay();
  const end = addDays(today, days);

  const color = row => (row.kind === 'income' ? C.accent : CATEGORY_COLORS[row.category || 'Other'] || CASH);
  const foreign = rows.filter(r => r.kind === 'expense' && r.inBase !== null && r.currency !== base);
  const currencies = [...new Set(foreign.map(r => r.currency))];
  const foreignLine = currencies.length === 0 ? null : (() => {
    const converted = foreign.reduce((sum, r) => sum + r.inBase, 0);
    if (currencies.length === 1) {
      const amount = foreign.reduce((sum, r) => sum + r.amount, 0);
      return s.ovForeign(fmt.amount(amount, currencies[0]), fmt.money(converted), fmt.number(converted / amount, 2));
    }
    return s.ovForeign(currencies.map(code => fmt.amount(foreign.filter(r => r.currency === code).reduce((x, r) => x + r.amount, 0), code)).join(' + '),
      fmt.money(converted), null);
  })();
  const approx = currencies.length > 0 ? '≈ ' : '';

  const week = rows.filter(r => r.inDays !== null && r.inDays <= 6);
  const later = rows.filter(r => r.inDays === null || r.inDays > 6);
  const sum = list => list.filter(r => r.kind === 'expense' && r.inBase !== null).reduce((x, r) => x + r.inBase, 0);
  const hasForeign = list => list.some(r => r.kind === 'expense' && r.currency !== base);
  const biggest = later.filter(r => r.kind === 'expense' && r.inBase !== null).map(r => r.inBase).sort((a, b) => b - a)[1] ?? Infinity;

  const when = row => (row.inDays === null
    ? fmt.monthName(row.month)
    : row.inDays <= 0 ? s.upcomingToday : shortDay(t, row.date));
  const amountOf = row => (row.kind === 'income' ? '+' : '') + fmt.amount(row.amount, row.currency);
  const dot = row => h('span', {
    style: css({ width: compact ? '8px' : '9px', height: compact ? '8px' : '9px', flex: '0 0 auto', borderRadius: '50%', background: color(row) })
  });
  const sub = row => [row.kind === 'expense' ? expenseLabel(t, row.category || 'Other') : null, row.posted ? s.upcomingPosted : null]
    .filter(Boolean).join(' · ');

  const head = (text, list) => h('div', {
    style: css({
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: compact ? '6px 0 4px' : '0 0 8px', borderBottom: `1px solid ${C.ghost}`
    })
  },
    h('span', { style: css({ fontFamily: MONO, fontSize: compact ? '10.5px' : '11px', letterSpacing: compact ? '1.1px' : '1.2px', color: C.textDim }) }, text),
    h('span', { style: css({ fontFamily: MONO, fontSize: compact ? '12px' : '12.5px', fontWeight: 700 }) },
      (hasForeign(list) ? '≈ ' : '') + fmt.money(sum(list)))
  );

  const weekRow = (row, i, list) => h('div', {
    key: row.id,
    title: row.posted ? s.upcomingPosted : null,
    style: css(compact
      ? { display: 'flex', alignItems: 'center', gap: '10px', minHeight: '48px', borderBottom: `1px solid ${C.line}` }
      : {
        display: 'grid', gridTemplateColumns: '10px minmax(0,1fr) auto 70px', gap: '12px', alignItems: 'center',
        padding: '11px 0', borderBottom: i < list.length - 1 ? `1px solid ${C.line}` : 'none'
      })
  },
    dot(row),
    h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }) },
      h('span', { style: css({ fontSize: compact ? '13px' : '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.label),
      sub(row) && h('span', { key: 's', style: css({ fontSize: compact ? '11px' : '12px', color: C.muted }) }, sub(row))
    ),
    h('div', { style: css({ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
      h('span', { style: css({ fontFamily: MONO, fontSize: '13px', color: row.kind === 'income' ? C.accent : C.text, whiteSpace: 'nowrap' }) }, amountOf(row)),
      row.currency !== base && row.inBase !== null && h('span', { key: 'c', style: css({ fontSize: '11.5px', color: C.muted }) }, '≈ ' + fmt.money(row.inBase)),
      compact && h('span', {
        key: 'd',
        style: css({ fontSize: '11px', fontWeight: row.inDays <= 0 ? 700 : 400, color: row.inDays <= 0 ? C.gold : C.textDim })
      }, when(row))
    ),
    !compact && h('span', {
      key: 'd',
      style: css({ textAlign: 'right', fontSize: '12px', fontWeight: row.inDays <= 0 ? 700 : 400, color: row.inDays <= 0 ? C.gold : C.textDim })
    }, when(row))
  );

  const laterRow = (row, i, list) => h('div', {
    key: row.id,
    title: row.posted ? s.upcomingPosted : null,
    style: css(compact
      ? { display: 'flex', alignItems: 'center', gap: '10px', minHeight: '44px', borderBottom: `1px solid ${C.line}`, opacity: row.posted ? 0.65 : 1 }
      : {
        display: 'grid', gridTemplateColumns: '10px minmax(0,1fr) auto 70px', gap: '12px', alignItems: 'center',
        padding: '9px 0', borderBottom: i < list.length - 1 ? `1px solid ${C.line}` : 'none', opacity: row.posted ? 0.65 : 1
      })
  },
    dot(row),
    h('span', { style: css({ flex: 1, minWidth: 0, fontSize: compact ? '13px' : '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.label),
    compact && h('span', { key: 'd', style: css({ fontSize: '11px', color: C.textDim, whiteSpace: 'nowrap' }) }, when(row)),
    h('span', {
      style: css({
        width: compact ? '76px' : 'auto', textAlign: 'right', fontFamily: MONO, fontSize: '13px', whiteSpace: 'nowrap',
        fontWeight: row.inBase !== null && row.inBase >= biggest ? 700 : 400, color: row.kind === 'income' ? C.accent : C.text
      })
    }, amountOf(row)),
    !compact && h('span', { key: 'd', style: css({ textAlign: 'right', fontSize: '12px', color: C.textDim }) }, when(row))
  );

  const totalBlock = h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: compact ? '2px' : '3px' }) },
    !compact && label(s.upcomingLeaving, false),
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '18px' : '24px', fontWeight: 700 }) }, approx + fmt.money(total)),
    foreignLine && h('div', { key: 'f', style: css({ fontSize: compact ? '10.5px' : '11.5px', color: C.muted }) }, foreignLine)
  );

  const header = h('div', { style: css({ display: 'flex', alignItems: compact ? 'flex-start' : 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }) },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: compact ? '3px' : '4px' }) },
      title(compact, s.upcoming),
      h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: C.muted }) },
        compact ? s.ovUpcomingSubShort(days) : s.ovUpcomingSub(days, shortDay(t, today), shortDay(t, end)))
    ),
    rows.length > 0 && totalBlock
  );

  if (rows.length === 0) return box(compact, {}, header, emptyLine(s.upcomingEmpty));

  const notes = unconverted > 0 && h('div', { key: 'u', style: css({ fontSize: '11px', color: C.muted }) }, s.fixedUnconverted(unconverted));

  if (compact) {
    const shown = state.upcomingAll ? later : later.slice(0, 3);
    return box(true, { gap: '10px' },
      header,
      week.length > 0 && h('div', { key: 'w' }, head(s.ovThisWeek, week), ...week.map(weekRow)),
      later.length > 0 && h('div', { key: 'l' }, head(s.ovLater, later), ...shown.map(laterRow)),
      later.length > 3 && h('button', {
        key: 'more', type: 'button', onclick: actions.toggleUpcomingAll,
        style: css({
          minHeight: '44px', background: 'none', border: 'none', fontFamily: MONO, fontSize: '11.5px', fontWeight: 700,
          letterSpacing: '.6px', color: C.accent, cursor: 'pointer'
        })
      }, state.upcomingAll ? s.ovCollapse : s.ovShowAll(later.length)),
      notes
    );
  }

  // The 45 days as a line: a dot on the day each charge lands, the week ahead shaded.
  const dated = rows.filter(r => r.inDays !== null);
  const top = Math.max(1, ...dated.map(r => r.inBase || 0));
  const ticks = [];
  for (let d = 7; d < days - 3; d += 7) ticks.push(d);
  const timeline = h('div', { style: css({ position: 'relative', height: '46px', margin: '0 6px' }) },
    h('div', { style: css({ position: 'absolute', left: 0, right: 0, top: '20px', height: '6px', borderRadius: '3px', background: C.stroke }) }),
    h('div', { style: css({ position: 'absolute', left: 0, width: pct(7 / days), top: '20px', height: '6px', borderRadius: '3px 0 0 3px', background: C.strokeHi }) }),
    ...dated.slice().sort((a, b) => (a.inBase || 0) - (b.inBase || 0)).map(row => {
      const size = row.inDays <= 0 ? 14 : (row.inBase || 0) >= top * 0.6 ? 16 : 12;
      return h('div', {
        key: 'd' + row.id,
        title: row.label + ' · ' + amountOf(row) + ' · ' + when(row),
        style: css({
          position: 'absolute', left: pct(clamp(row.inDays, 0, days) / days), top: (23 - size / 2) + 'px',
          width: size + 'px', height: size + 'px', marginLeft: -size / 2 + 'px', borderRadius: '50%',
          background: row.inDays <= 0 ? C.gold : color(row), boxShadow: `0 0 0 3px ${C.card}`
        })
      });
    }),
    h('div', { style: css({ position: 'absolute', left: 0, top: 0, fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: C.gold }) }, s.ovToday),
    ...ticks.map(d => h('div', {
      key: 't' + d,
      style: css({ position: 'absolute', left: pct(d / days), top: 0, transform: 'translateX(-50%)', fontFamily: MONO, fontSize: '11px', color: C.muted })
    }, shortDay(t, addDays(today, d)))),
    h('div', { style: css({ position: 'absolute', right: 0, top: 0, fontFamily: MONO, fontSize: '11px', color: C.muted }) }, shortDay(t, end))
  );

  return box(false, { gap: '18px' },
    header,
    timeline,
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '14px 28px' }) },
      week.length > 0 && h('div', { key: 'w', style: css({ display: 'flex', flexDirection: 'column', minWidth: 0 }) },
        head(s.ovThisWeek, week), ...week.map(weekRow)),
      later.length > 0 && h('div', { key: 'l', style: css({ display: 'flex', flexDirection: 'column', minWidth: 0 }) },
        head(s.ovLater, later), ...later.map(laterRow))
    ),
    notes
  );
}

// ---------------------------------------------------------------- review ---

/**
 * Reads a review into its parts. Written as "• [+] 33% :: przychodu
 * zaoszczędzone" since 1.68; older reviews were plain points and a summary,
 * and still read -- every line that does not match is kept as it is.
 */
export function parseReview(text) {
  const out = { summary: [], rows: [], action: null };
  /*
   * Points and the action written inline -- "… zysku. • Głównym wydatkiem … →
   * W nadchodzącym tygodniu …", one paragraph, as reviews before 1.68 often
   * came back -- are put on lines of their own first.
   */
  const lines = String(text || '')
    .replace(/\s+•\s+/g, '\n• ')
    .replace(/\s+→\s*/g, '\n→ ')
    .split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('→')) { out.action = line.replace(/^→\s*/, ''); continue; }
    const point = /^[•\-*]\s*(?:\[([+\-=])\]\s*)?(.+?)\s*::\s*(.+)$/.exec(line);
    if (point) { out.rows.push({ tone: point[1] || '=', figure: point[2], text: point[3] }); continue; }
    if (/^[•\-*]\s+/.test(line)) {
      const sentence = line.replace(/^[•\-*]\s+/, '');
      out.rows.push({ tone: null, figure: keyFigure(sentence), text: sentence });
      continue;
    }
    out.summary.push(line);
  }
  return out;
}

/*
 * The first amount or percentage in a plain point, for the figure column of a
 * review written before points carried one. "100 procent" reads as "100%".
 */
function keyFigure(sentence) {
  const found = /(\d{1,3}(?:[  ]\d{3})+|\d+)(?:[.,]\d+)?\s?(%|procent\w*|percent|PLN|zł|USD|EUR|GBP|\$|€)/i.exec(sentence);
  if (!found) return null;
  return found[0].replace(/\s?(procent\w*|percent)$/i, '%').replace(/ /g, ' ');
}

export function reviewCard({ state, t, actions, compact = false }) {
  const s = t.shell;
  if (!state.review) { actions.loadReview(); return null; }
  const r = state.review;
  if (!r.offered) return null;
  const waiting = r.nextAt && Date.parse(r.nextAt) > Date.now();
  const canAsk = r.available && !waiting;
  const parts = r.review ? parseReview(r.review.text) : null;
  const toneColor = tone => (tone === '+' ? C.accent : tone === '-' ? DANGER_TEXT : C.text);
  const ask = canAsk && h('div', { key: 'ask' },
    button(state.reviewLoading ? s.rvWriting : s.rvAsk, actions.requestReview, {
      style: { opacity: state.reviewLoading ? 0.5 : 1, ...(compact ? { padding: '14px', borderRadius: '12px' } : {}) }
    }));

  const header = h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }) },
    h('div', {
      style: css({ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'flex-start' : 'baseline', gap: compact ? '3px' : '12px', flexWrap: 'wrap' })
    },
      title(compact, s.rvTitle),
      h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: C.muted }) },
        compact && r.review ? s.rvByClaude(s.dateFull(r.review.createdAt)) : s.rvSub)
    ),
    !compact && ask
  );

  const body = !r.available
    ? h('div', { key: 'plus', style: css({ fontSize: '12.5px', color: C.muted }) }, s.rvPlus)
    : !parts
      ? h('div', { key: 'none', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.rvNone)
      : h('div', { key: 'text', style: css({ display: 'flex', flexDirection: 'column', gap: compact ? '6px' : '14px', maxWidth: '760px' }) },
        ...parts.summary.map((line, i) => h('div', {
          /* The summary leads, a step brighter than the points under it. */
          key: 's' + i, style: css({ fontSize: compact ? '13px' : '14px', lineHeight: 1.6, color: C.text })
        }, line)),
        parts.rows.length > 0 && h('div', { key: 'rows', style: css({ display: 'flex', flexDirection: 'column' }) },
          ...parts.rows.map((row, i) => h('div', {
            key: 'r' + i,
            style: css({
              display: 'grid', gridTemplateColumns: `${compact ? 68 : 96}px minmax(0,1fr)`, gap: compact ? '12px' : '16px',
              padding: compact ? '9px 0' : '11px 0', borderBottom: i < parts.rows.length - 1 ? `1px solid ${C.line}` : 'none'
            })
          },
            h('div', {
              style: css({ fontFamily: MONO, fontSize: compact ? '14px' : '16px', fontWeight: 700, color: toneColor(row.tone), wordBreak: 'break-word' })
            }, row.figure || '•'),
            h('div', { style: css({ fontSize: compact ? '12.5px' : '13.5px', lineHeight: 1.55, color: C.textDim }) }, row.text)
          ))),
        parts.action && h('div', {
          key: 'act',
          style: css({
            display: 'flex', gap: compact ? '10px' : '12px', alignItems: 'flex-start', padding: compact ? '11px 12px' : '12px 14px',
            borderRadius: compact ? '12px' : '11px', background: GAIN_BG, border: `1px solid ${GAIN_LINE}`
          })
        },
          h('span', { style: css({ fontFamily: MONO, fontSize: '14px', fontWeight: 700, color: C.accent }) }, '→'),
          h('div', { style: css({ fontSize: compact ? '12.5px' : '13.5px', lineHeight: 1.55, color: '#D6E8E1' }) },
            !compact && h('span', { key: 'b', style: css({ fontWeight: 700 }) }, s.rvForWeek + ' '), parts.action)
        )
      );

  /* The fine print sits at the foot of the card, under a rule, apart from what the review says. */
  const foot = h('div', {
    style: css({
      marginTop: 'auto', paddingTop: compact ? '10px' : '12px', borderTop: `1px solid ${C.line}`,
      fontSize: compact ? '11px' : '11.5px', lineHeight: 1.5, color: C.faint
    })
  },
    (r.review && !compact ? s.rvPrepared(s.dateFull(r.review.createdAt), waiting ? s.dateFull(r.nextAt) : null) + ' ' : '') + s.rvPrivacy);

  return box(compact, { height: compact ? 'auto' : '100%', boxSizing: 'border-box', gap: compact ? '8px' : '14px' },
    header, body, compact && ask, foot);
}

// ---------------------------------------------------------- what-if card ---

export function whatIfCard({ state, metrics, t, fmt, actions, compact = false }) {
  const s = t.shell;
  if (!metrics?.runway || metrics.isEmpty) return null;
  const w = state.whatIf;
  const result = whatIfFor(state, metrics);
  const { scenario: scen, baseline: base } = result;
  const changed = w.extra > 0 || w.invest > 0;
  const years = s.wiYearsValue(w.years);
  const per = v => s.wiPerMonthShort(fmt.money(v));
  const monthOf = m => fmt.monthName(shiftMonth(currentMonth(), m));
  const debtFree = scen.debtFree === null ? s.wiBeyond : scen.debtFree === 0 ? s.wiNoDebt : monthOf(scen.debtFree);
  const open = h('button', {
    type: 'button', onclick: () => actions.openWhatIf(true),
    style: css({
      height: compact ? '46px' : '42px', border: 'none', borderRadius: compact ? '12px' : '10px', background: C.accent,
      color: C.bg, fontFamily: MONO, fontSize: '12px', fontWeight: 700, letterSpacing: '.8px', cursor: 'pointer', width: '100%'
    })
  }, s.wiOpen);

  if (compact) {
    return box(true, { gap: '10px' },
      h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }) },
        title(true, s.wiTitle),
        h('div', { style: css({ fontSize: '11px', color: C.muted }) }, s.wiInYears(years))
      ),
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '20px', fontWeight: 700 }) }, fmt.money(scen.end.netWorth)),
        changed && h('span', { key: 'b', style: css({ fontSize: '12px', color: C.muted }) }, s.wiInstead(fmt.money(base.end.netWorth)))
      ),
      h('div', { style: css({ fontSize: '12.5px', color: changed ? (result.gain >= 0 ? C.accent : DANGER_TEXT) : C.muted }) },
        changed ? s.wiChangeGives(fmt.signed(result.gain)) : s.wiSetSliders),
      open
    );
  }

  return box(false, { height: '100%', boxSizing: 'border-box' },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
      title(false, s.wiTitle),
      h('div', { style: css({ fontSize: '12.5px', color: C.muted }) },
        s.wiSummary(per(w.extra), per(w.invest), fmt.number(w.ret, 1) + '%', years))
    ),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
      label(s.wiKpiEnd(years), false),
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '24px', fontWeight: 700 }) }, fmt.money(scen.end.netWorth)),
        changed && h('span', { key: 'b', style: css({ fontSize: '12.5px', color: C.muted }) }, s.wiInstead(fmt.money(base.end.netWorth))))
    ),
    changed
      ? h('div', { key: 'tiles', style: css({ display: 'flex', gap: '10px' }) },
        h('div', { style: css({ flex: 1, padding: '10px 12px', borderRadius: '10px', background: GAIN_BG, display: 'flex', flexDirection: 'column', gap: '3px' }) },
          h('span', { style: css({ fontSize: '11.5px', color: GAIN_LABEL }) }, s.wiChangeGivesLabel),
          h('span', { style: css({ fontFamily: MONO, fontSize: '15px', fontWeight: 700, color: result.gain >= 0 ? C.accent : DANGER_TEXT }) }, fmt.signed(result.gain))),
        h('div', { style: css({ flex: 1, padding: '10px 12px', borderRadius: '10px', background: C.panel, display: 'flex', flexDirection: 'column', gap: '3px' }) },
          h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.wiDebtFreeLabel),
          h('span', { style: css({ fontFamily: MONO, fontSize: '15px', fontWeight: 700 }) }, debtFree)))
      : h('div', { key: 'hint', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.wiSetSliders),
    scen.short !== null && h('div', {
      key: 'short',
      style: css({
        display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '10px',
        background: DANGER_BG, border: `1px solid ${DANGER_LINE}`
      })
    },
      h('span', { style: css({ width: '7px', height: '7px', borderRadius: '50%', flex: '0 0 7px', marginTop: '6px', background: C.danger }) }),
      h('span', { style: css({ fontSize: '12.5px', lineHeight: 1.5, color: '#E3BEC3' }) }, s.wiShortAround(monthOf(scen.short)))),
    h('div', { style: css({ flex: 1 }) }),
    open
  );
}

/** The simulator on a page of its own, with the way back. */
export function simulatorPage({ t, actions, compact = false }, panel) {
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '16px' }) },
    h('button', {
      type: 'button', onclick: () => actions.openWhatIf(false),
      style: css({
        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px',
        height: compact ? '44px' : '36px', padding: '0 14px 0 10px', borderRadius: compact ? '12px' : '9px',
        border: `1px solid ${C.stroke}`, background: C.card, color: C.textDim, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: '13px', fontWeight: 600
      })
    }, h('span', { style: css({ fontSize: '17px', lineHeight: 1 }) }, '‹'), t.shell.wiBack),
    panel
  );
}
